/**
 * AssumptionsDrawer.jsx — Method & Assumptions surface for any module report.
 *
 * Collapsed by default. Premium, calm styling. Shows:
 *   - Coverage (full org / sample, with N)
 *   - Model in use
 *   - Context mode (standard / light / none)
 *   - PRE / Uncertainty mode
 *   - Social influence
 *   - Synthetic disclaimer
 *
 * Pass any subset of fields — empty fields are hidden.
 *
 * Use this in HRLabReport (PeopleLab) at the bottom of the report and reusable
 * for MarketLab / PharmaLab. Pairs with <ReliabilitySummary mode="full"/>.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { ChevronDown, ChevronUp, Settings2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { CONTEXT_MODE_LABELS } from '../../lib/metricLabels';

function deriveFields(simulation) {
  if (!simulation) return [];

  const config = simulation.config || {};
  const contextMode = config.context_mode || simulation.context_mode || null;
  const totalN = simulation.population_total_n;
  const actualN = simulation.actual_n ?? simulation.total_responses ?? simulation.sample_size;
  const isFull = !!simulation.is_full_census;
  const incomplete = !!simulation.incomplete_census_flag;
  const coverageRate = simulation.coverage_rate;

  const fields = [];

  // Coverage
  if (actualN != null && totalN != null) {
    const cov = coverageRate != null ? (coverageRate * 100).toFixed(0) : ((actualN / Math.max(totalN, 1)) * 100).toFixed(0);
    fields.push({
      label: 'Coverage',
      value: incomplete
        ? `${actualN}/${totalN} (${cov}%) · incomplete`
        : isFull
        ? `Full census · ${actualN}/${totalN}`
        : `Sample · ${actualN}/${totalN} (${cov}%)`,
      tone: incomplete ? 'amber' : isFull ? 'emerald' : 'slate',
    });
  } else if (actualN != null) {
    fields.push({
      label: 'Sample size',
      value: `${actualN.toLocaleString()} responses`,
      tone: 'slate',
    });
  }

  // Model
  const model = config.model || config.llm_model || simulation.model;
  if (model) {
    fields.push({ label: 'Model', value: model, tone: 'slate' });
  }

  // Context
  if (contextMode) {
    fields.push({
      label: 'Context',
      value: CONTEXT_MODE_LABELS[contextMode] || contextMode,
      tone: contextMode === 'none' ? 'slate' : 'sky',
    });
  }

  // Uncertainty-aware (PRE)
  const isPRE =
    !!simulation.pre_mode_used ||
    config.use_pre ||
    config.pre_mode_enabled ||
    simulation.output_mode === 'probs';
  fields.push({
    label: 'Result mode',
    value: isPRE ? 'Uncertainty-aware (probability-weighted)' : 'Sampled outcomes',
    tone: isPRE ? 'purple' : 'slate',
  });

  // Social influence
  if (simulation.social_enabled != null) {
    fields.push({
      label: 'Social influence',
      value: simulation.social_enabled ? 'Enabled' : 'Disabled',
      tone: simulation.social_enabled ? 'blue' : 'slate',
    });
  }

  // Web context exposure
  if (simulation.web_context_exposure_rate != null) {
    fields.push({
      label: 'Web context exposure',
      value: `${(simulation.web_context_exposure_rate * 100).toFixed(0)}%`,
      tone: 'sky',
    });
  }

  return fields;
}

const TONE_BADGE = {
  emerald: 'bg-positive-soft text-positive-soft-foreground border-positive-border',
  amber: 'bg-warning-soft text-warning-soft-foreground border-warning-border',
  sky: 'bg-blue-50 text-blue-700 border-blue-200',
  purple: 'bg-blue-50 text-blue-700 border-blue-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  slate: 'bg-slate-50 text-slate-700 border-slate-200',
};

export default function AssumptionsDrawer({
  simulation,
  extraFields = [],
  title = 'Method & Assumptions',
  description = 'How this report was generated and what to keep in mind when interpreting it.',
  defaultOpen = false,
  className,
  testId = 'assumptions-drawer',
}) {
  const [open, setOpen] = useState(defaultOpen);
  const fields = [...deriveFields(simulation), ...(extraFields || [])];

  if (fields.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className={cn('border border-slate-200 dark:border-border', className)} data-testid={testId}>
        <CollapsibleTrigger asChild>
          <CardHeader className="py-3 cursor-pointer hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Settings2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-300 truncate">
                  {title}
                </CardTitle>
                <Badge variant="outline" className="text-[10px] text-slate-400 flex-shrink-0">
                  {fields.length} field{fields.length === 1 ? '' : 's'}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0" data-testid={`${testId}-toggle`}>
                {open ? (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
              </Button>
            </div>
            {description && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{description}</p>
            )}
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {fields.map((f, i) => (
                <div
                  key={i}
                  className="p-2.5 rounded-lg bg-slate-50/60 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700"
                  data-testid={`${testId}-field-${i}`}
                >
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">
                    {f.label}
                  </p>
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 leading-snug">
                    {f.value}
                  </p>
                  {f.tone && f.badge && (
                    <Badge
                      variant="outline"
                      className={cn('mt-1.5 text-[10px]', TONE_BADGE[f.tone] || TONE_BADGE.slate)}
                    >
                      {f.badge}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 dark:text-slate-500 pt-2 border-t border-slate-100 dark:border-slate-700 leading-relaxed">
              Results are synthetic and directional. Segment data masked below 30 respondents.
            </p>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
